<template>
  <Modal
    :open="props.open"
    @close-modal="$emit('close-modal')"
    customHeight="h-144"
    customWidth="lg:w-180"
  >
    <template #modal-header>
      <div class="">
        <div class="flex items-center">
          <Icon name="Upload" class="w-4 h-4 mr-1 stroke-info" />
          <div class="text-sm font-inter font-semibold text-info">Upload</div>
        </div>
        <div class="font-league tracking-[-.56px] font-semibold text-[1.75rem] text-strongest">
          Upload Documents
        </div>
      </div>
    </template>

    <template #modal-body>
      <div class="p-4" v-if="!is_loading">
        <div class="mb-2">
          <BaseInput
            v-model="selected_file_category"
            label="File Category"
            field_type="dropdown"
            variant="secondary"
            :dropdown_options="modal_config.file_category_options"
          />
        </div>
        <div v-if="error_message" class="mb-2 font-inter text-error text-sm flex items-center">
          <Icon name="Asterisk" class="mr-2 h-[14px] w-[14px] -skew-x-12 stroke-error" />{{
            error_message
          }}
        </div>

        <FileDropper
          v-if="!selected_files.length"
          class="bg-container-bg"
          @files-selected="handleUploadedFiles"
        />
        <div v-else>
          <div class="flex flex-col gap-2">
            <div
              v-for="(file, index) in selected_files"
              :key="index"
              class="text-strongest bg-container-bg p-4 rounded-lg flex justify-between items-center"
            >
              <div>{{ file.name }}</div>
              <div>
                <Button
                  variant="paper-primary"
                  class="items-center flex"
                  size="sm"
                  @click="() => selected_files.splice(index, 1)"
                >
                  <template #icon>
                    <Icon name="Trash2" class="w-4 h-4 stroke-error" />
                  </template>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="flex h-full items-center">
        <LoadingSpinner
          :hide-overlay="true"
          :show="is_loading"
          message="Uploading Documents..."
          class="mt-10"
        />
      </div>
    </template>
    <template #modal-footer>
      <div class="flex justify-end gap-2">
        <Button @click="$emit('close-modal')" variant="paper-primary" size="sm" class="">
          <template #label>Cancel</template>
        </Button>
        <Button @click="handleConfirm()" variant="primary-grey" size="sm" class="">
          <template #label>Confirm</template>
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
// IMPORTS
import { ref, watch } from "vue";
import { Modal, Icon, Button, BaseInput, FileDropper, LoadingSpinner } from "@/components";
import { useCrudStore, useUIStore, useSchemaStore, useDataStore } from "@/stores";
import useFileUtility from "@/composables/useFileUtility";
const { uploadFile_R2 } = useFileUtility();

//STORES
const crudStore = useCrudStore();
const UIStore = useUIStore();
const schemaStore = useSchemaStore();
const dataStore = useDataStore();

const selected_file_category = ref("");
const selected_files = ref([]);
const error_message = ref("");
const is_loading = ref(false);

//EMITS AND PROPS
const emit = defineEmits(["close-modal", "refresh"]);
const props = defineProps({
  open: { type: Boolean, required: true },
  modal_config: { type: Object, required: true },
});

watch(
  () => props.open,
  (newVal) => {
    if (!newVal) {
      selected_file_category.value = "";
      selected_files.value = [];
      error_message.value = "";
      is_loading.value = false;
    }
  },
  { immediate: true }
);

// watch selected file category and clear error message on change as long as the error is related to file category selection
watch(selected_file_category, (newVal) => {
  if (error_message.value === "Please select a file category." && newVal) {
    error_message.value = "";
  }
});

async function handleUploadedFiles(files) {
  selected_files.value = files;
  error_message.value = "";
}

// COMPONENT LOGIC AND FUNCTIONS
async function handleConfirm() {
  try {
    if (!selected_file_category.value) {
      error_message.value = "Please select a file category.";
      return;
    }

    if (!selected_files.value.length) {
      error_message.value = "Please select at least one file to upload.";
      return;
    }

    error_message.value = "";
    is_loading.value = true;

    await Promise.all(
      selected_files.value.map((file) =>
        uploadFile_R2({
          file: file,
          type:
            selected_file_category.value === "Accreditation Support"
              ? "Accreditation"
              : "General Documents",
          details: {
            file_category: selected_file_category.value,
            visible_to_compliance: false,
            user_id: dataStore.contact.user_id,
            contact_id: dataStore.contact.record_id,
            account_id: dataStore.contact.account_id,
          },
        })
      )
    );

    emit("refresh");
    emit("close-modal");
  } catch (error) {
    var alert = {
      type: "error",
      message: `Failed to update document.`,
    };
    UIStore.animateNotificationAlert(alert);
    emit("close-modal");
  }
}
</script>
